import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui/IconButton';
import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

export type CalloutTone = 'neutral' | 'info' | 'warning' | 'danger';

const TONE: Record<CalloutTone, string> = {
  neutral:
    'border-dashed border-border-strong bg-surface text-foreground-muted',
  // Solid and uncolored on purpose: `info` states scope the user should know
  // about, so it must not borrow the alarm of `warning`, nor the dashed
  // "optional tip" look of `neutral`.
  info: 'border-border-strong bg-surface text-foreground-subtle',
  warning: 'border-warning-6 bg-warning-3 text-foreground-subtle',
  danger: 'border-danger-6 bg-danger-3 text-foreground-subtle',
};

const ICON_TONE: Record<CalloutTone, string> = {
  neutral: 'text-foreground-muted',
  info: 'text-foreground-subtle',
  warning: 'text-warning-11',
  danger: 'text-danger-11',
};

export function Callout({
  tone,
  icon,
  children,
  onDismiss,
}: {
  tone: CalloutTone;
  /** Omit where the text alone carries the message. */
  icon?: React.ReactNode;
  children: React.ReactNode;
  onDismiss?: () => void;
}) {
  return (
    <XStack
      className={cn('items-start gap-2 rounded-lg border p-3', TONE[tone])}
    >
      {icon && (
        <span className={cn('mt-px shrink-0', ICON_TONE[tone])}>{icon}</span>
      )}
      <div className="flex-1 text-xs leading-relaxed">{children}</div>
      {onDismiss && (
        <IconButton
          icon={Icons.cancel}
          size="xs"
          variant="ghost"
          tooltip="Dismiss"
          onClick={onDismiss}
        />
      )}
    </XStack>
  );
}
